<template>
  <div class="w-full border border-gray-200 rounded bg-gray-50 dark-bg-custom">
    <div class="flex justify-between items-center p-3 mb-2">
      <h6 class="text-sm font-medium text-blue-900 m-0 flex items-center">
        <v-icon small class="mr-1">mdi-cash-plus</v-icon>
        Additional Charges
      </h6>
      <v-btn
        x-small
        color="primary"
        outlined
        @click="addAdditionalCharge"
        class="normal-case font-medium text-xs"
        v-if="localCharges?.length < 10"
      >
        <v-icon x-small class="mr-1">mdi-plus</v-icon>
        Add
      </v-btn>
    </div>

    <div class="p-2">
      <div
        v-for="(charge, index) in localCharges"
        :key="`additional-charge-${index}`"
        class="mb-4 p-3 border rounded bg-white relative"
      >
        <v-btn
          v-if="localCharges.length > 1"
          icon
          x-small
          color="error"
          @click="removeAdditionalCharge(index)"
          class="absolute top-0 right-0 ma-1"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>

        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="charge.name"
              label="Charge Name"
              outlined
              dense
              placeholder="e.g., Documentation Fee"
              @input="emitChanges"
              hide-details
              class="text-xs mb-2"
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="charge.amount"
              label="Amount"
              type="number"
              outlined
              dense
              prefix="₹"
              @input="emitChanges"
              hide-details
              class="text-xs mb-2 font-weight-bold"
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="2">
            <v-menu
              v-model="charge.dueDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="charge.dueDate"
                  label="Due Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  hide-details
                  class="text-xs mb-2"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="charge.dueDate"
                @input="
                  charge.dueDateMenu = false
                  emitChanges()
                "
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="charge.metaData.gstAmount"
              label="Tax (GST)"
              type="number"
              outlined
              dense
              @input="emitChanges"
              hide-details
              class="text-xs mb-2"
            ></v-text-field>
          </v-col>

          <v-col cols="6" md="2" v-if="showLinkWithEmi">
            <v-checkbox
              v-model="charge.linkWithEmi"
              label="Link with EMI"
              dense
              hide-details
              class="mt-0 pt-1"
              @change="emitChanges"
            ></v-checkbox>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="charge.metaData.discountAmount"
              label="Discount"
              type="number"
              outlined
              dense
              @input="emitChanges"
              hide-details
              class="text-xs"
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="charge.metaData.waiverAmount"
              label="Waiver"
              type="number"
              outlined
              dense
              @input="emitChanges"
              hide-details
              class="text-xs"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="charge.remarks"
              label="Remarks"
              outlined
              dense
              placeholder="Internal notes"
              @input="emitChanges"
              hide-details
              class="text-xs"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2" class="flex items-center justify-end">
            <div class="text-right">
              <span class="text-caption text-gray-500">Net Total:</span>
              <div class="text-sm font-bold text-blue-900">
                ₹{{ formatCurrencyValue(calculateNetTotal(charge)) }}
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </div>

    <div class="p-4 bg-blue-50 dark-bg-custom border-t" v-if="localCharges.length > 0">
      <div class="flex flex-column items-end">
        <div class="w-full md:w-1/3">
          <div class="flex justify-between py-1 border-b border-blue-100">
            <span class="text-xs font-medium text-gray-600">Base Total:</span>
            <span class="text-sm font-semibold text-gray-900"
              >₹{{ formatCurrencyValue(totalBaseAmount) }}</span
            >
          </div>
          <div class="flex justify-between py-2 mt-1">
            <span class="text-sm font-bold text-blue-900 uppercase tracking-wide"
              >Net Payable:</span
            >
            <span class="text-lg font-bold text-blue-800"
              >₹{{ formatCurrencyValue(totalNetPayable) }}</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import { getAdditionalCharges } from '@/utils/workforce/utils'

export default {
  name: 'AdditionalChargesSection',
  props: {
    charges: {
      type: Array,
      default: () => []
    },
    loanAmount: {
      type: [String, Number],
      default: 0
    },
    formatCurrency: {
      type: Function,
      default: (amount) => `₹${amount?.toLocaleString() || '0'}`
    },
    emiAmount: String,
    showLinkWithEmi: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localCharges: [
        {
          name: '',
          amount: 0,
          dueDate: null,
          dueDateMenu: false,
          linkWithEmi: false,
          remarks: '',
          metaData: {
            gstAmount: 0,
            discountAmount: 0,
            waiverAmount: 0
          }
        }
      ]
    }
  },
  computed: {
    totalBaseAmount() {
      return this.localCharges.reduce((sum, charge) => {
        return sum + (parseFloat(charge.amount) || 0)
      }, 0)
    },
    totalNetPayable() {
      return this.localCharges.reduce((sum, charge) => {
        return sum + this.calculateNetTotal(charge)
      }, 0)
    }
  },
  watch: {
    charges: {
      handler(newCharges) {
        if (newCharges && newCharges.length > 0) {
          this.localCharges = newCharges.map((charge) => ({
            ...charge,
            dueDateMenu: false,
            metaData: charge.metaData || {
              gstAmount: 0,
              discountAmount: 0,
              waiverAmount: 0
            }
          }))
        } else {
          // Only reset to default if we don't already have charges
          if (
            !this.localCharges.length ||
            (this.localCharges.length === 1 &&
              !this.localCharges[0].name &&
              !this.localCharges[0].amount)
          ) {
            this.localCharges = [
              {
                name: '',
                amount: 0,
                dueDate: null,
                dueDateMenu: false,
                isDeduction: false,
                remarks: '',
                metaData: {
                  gstAmount: 0,
                  discountAmount: 0,
                  waiverAmount: 0
                }
              }
            ]
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    addAdditionalCharge() {
      this.localCharges.push({
        name: '',
        amount: 0,
        dueDate: null,
        dueDateMenu: false,
        linkWithEmi: false,
        remarks: '',
        metaData: {
          gstAmount: 0,
          discountAmount: 0,
          waiverAmount: 0
        }
      })
      this.emitChanges()
    },
    removeAdditionalCharge(index) {
      this.localCharges.splice(index, 1)
      this.emitChanges()
    },
    calculateNetTotal(charge) {
      const amount = parseFloat(charge.amount) || 0
      const gst = parseFloat(charge.metaData?.gstAmount) || 0
      const discount = parseFloat(charge.metaData?.discountAmount) || 0
      const waiver = parseFloat(charge.metaData?.waiverAmount) || 0
      return amount + gst - discount - waiver
    },
    formatCurrencyValue(value) {
      return new Intl.NumberFormat('en-IN').format(value.toFixed(2))
    },
    emitChanges() {
      // Create a clean copy for emit, removing temporary UI state like dueDateMenu
      const cleanCharges = this.localCharges.map((charge) => {
        // eslint-disable-next-line no-unused-vars
        const { dueDateMenu, ...rest } = charge
        return rest
      })
      this.$emit('update:charges', cleanCharges)
      this.$emit('charges-changed', cleanCharges)
    }
  }
}
</script>
